import { BaseAutocomplete as Autocomplete } from '@pcln/horizon'
import { useMachine } from '@xstate/react'
import CONFIG from 'isomorphic-config'
import fetch from 'isomorphic-unfetch'
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react'

import fireTypeaheadClickEvent, { fireAutocompleteOpenEvent } from '@/components/TypeAhead/ga4'
import useTypeAhead from '@/components/TypeAhead/useTypeAhead'
import useBootstrapData from '@/hooks/useBootstrapData'
import useClientSideSignedInData from '@/hooks/useClientSideSignedInData'
import useRecentSearchesFetched from '@/hooks/useRecentSearchesFetched'
import useRecentSearchesLocalStorage from '@/hooks/useRecentSearchesLocalStorage'
import useRecommendations from '@/hooks/useRecommendations'
import useRecommendedCityIds from '@/hooks/useRecommendedCityIds'
import analytics from '@/shared-utils/analytics'
import type { LOCATION_SEARCH_TYPE } from '@/types'

import geoMachine from '../../machines/currentLocationMachine'
import type { CityFilterItem, SelectedTopFilters } from '../Hotels/types'
import { DEFAULT_COLLISION_AVOIDANCE } from '../SearchForm/constants'
import { top20Filters } from '../TypeAhead/constants'
import { renderDisplayText } from '../TypeAhead/Dropdown/RenderCurrentLocation'
import { fireFilterBarChipGA4Event } from '../TypeAhead/FilterBar/ga4'
import type { TypeAheadConfigEndpoint } from '../TypeAhead/types.ts'
import { useFlightAirportNesting } from '../TypeAhead/useFlightAirportNesting'
import { cityIdExistsInTop20Filters } from '../TypeAhead/utils'
import { mapTypeToCode, recentSearchesKey } from './constants'
import { getSelectedChildPosition } from './getSelectedChildPosition'
import {
  buildAutocompleteDataAndListType,
  excludeNeighbourhoodRecentSearches,
  hasRecentSearches,
  hasRecommendedSearches,
  mergeRecentSearchesById,
  resolveClickListTypeForItem,
} from './utils/autocompleteData'

const MAX_AUTOCOMPLETE_RECENT_SEARCHES = 3

const AI_SEARCH_BUTTON_CONFIG = {
  VARIANT1: { label: 'Ask', linkName: 'ask_ai_mode_typeahead' },
  VARIANT2: { label: 'AI Mode', linkName: 'ai_mode_typeahead' },
} as const

export type AutocompleteWrapperOnItemSelectContext = {
  inputElement: HTMLInputElement | null
}

const GEO_MACHINE_INITIAL = {
  context: { error: null, cityData: null, position: null },
} as const

const AutocompleteWrapper = forwardRef<
  HTMLInputElement,
  {
    searchKey: string
    onItemSelect?: (
      item: LOCATION_SEARCH_TYPE | null,
      context?: AutocompleteWrapperOnItemSelectContext,
    ) => void
    initialSelectedItem?: LOCATION_SEARCH_TYPE
    typeAheadEndpoint?: TypeAheadConfigEndpoint
    errorMessage?: string
    label?: string
    isMobile?: boolean
    showFilterChips?: boolean
    selectedFilterChipData?: SelectedTopFilters
    setSelectedFilterChipData?: (value: SetStateAction<SelectedTopFilters>) => void
    showCurrentLocation?: boolean
    onGeolocationError?: (errorMessage: string | null) => void
    aiSearchButtonVariant?: 'VARIANT1' | 'VARIANT2'
    mergeRecentAndRecommendedSearches?: boolean
    preferRecentSearchesOnly?: boolean
    onRecentSearchImprovementImpression?: () => void
    hasAISearch?: boolean
    hasLTRSearch?: boolean
  }
>(function AutocompleteWrapper(
  {
    searchKey,
    typeAheadEndpoint = 'hotels',
    onItemSelect,
    initialSelectedItem,
    errorMessage,
    label,
    isMobile,
    showFilterChips = false,
    selectedFilterChipData,
    setSelectedFilterChipData,
    showCurrentLocation = false,
    onGeolocationError,
    aiSearchButtonVariant,
    mergeRecentAndRecommendedSearches = false,
    preferRecentSearchesOnly = false,
    onRecentSearchImprovementImpression,
    hasAISearch = false,
    hasLTRSearch = false,
  },
  ref,
) {
  const {
    webstats: { clientGUID },
    appName,
    appVersion,
    signInStatus: { signedIn: serverSignedIn },
  } = useBootstrapData()
  const { signedIn: clientSideSignedIn } = useClientSideSignedInData()
  const signedIn = serverSignedIn || clientSideSignedIn

  const [current, send] = useMachine(geoMachine, GEO_MACHINE_INITIAL)

  const displayFilterChips = !hasAISearch && showFilterChips

  // recent search
  const currentRecentSearchesKey = recentSearchesKey[typeAheadEndpoint]
  const [recentStartLocations, recentEndLocations] = useRecentSearchesLocalStorage(currentRecentSearchesKey)
  const recentSearchesLocalStorage = searchKey.includes('startLocation')
    ? recentStartLocations
    : recentEndLocations
  const [recentSearchesFetched, areRecentSearchesReady] = useRecentSearchesFetched(
    clientGUID,
    appName,
    appVersion,
    signedIn,
  )
  const isHotelEndpoint = typeAheadEndpoint === 'hotels' || typeAheadEndpoint === 'hotelsV2'
  const isHotelMergeRecentSearchFlow = isHotelEndpoint && mergeRecentAndRecommendedSearches
  const useImprovedRecentSearchFlow =
    isHotelEndpoint && (mergeRecentAndRecommendedSearches || preferRecentSearchesOnly)
  const recentSearches = useMemo(() => {
    const filterHotelRecentSearches = (items: LOCATION_SEARCH_TYPE[]) =>
      isHotelEndpoint ? excludeNeighbourhoodRecentSearches(items) : items

    if (useImprovedRecentSearchFlow) {
      if (!areRecentSearchesReady) {
        return []
      }
      if (hasRecentSearches(recentSearchesFetched)) {
        return mergeRecentSearchesById(
          filterHotelRecentSearches(recentSearchesFetched),
          filterHotelRecentSearches(recentSearchesLocalStorage),
          MAX_AUTOCOMPLETE_RECENT_SEARCHES,
        )
      }
      return filterHotelRecentSearches(recentSearchesLocalStorage).slice(0, MAX_AUTOCOMPLETE_RECENT_SEARCHES)
    }
    const recentsSource = isHotelEndpoint ? recentSearchesFetched : recentSearchesLocalStorage
    return filterHotelRecentSearches(recentsSource).slice(0, MAX_AUTOCOMPLETE_RECENT_SEARCHES)
  }, [
    isHotelEndpoint,
    useImprovedRecentSearchFlow,
    areRecentSearchesReady,
    recentSearchesFetched,
    recentSearchesLocalStorage,
  ])

  // recommended search
  const { recommendedCityIds } = useRecommendedCityIds({
    shouldFetchRecommendedCityIds: signedIn && !preferRecentSearchesOnly,
    typeAheadEndpoint,
  })

  const { data: recommendedSearches } = useRecommendations(recommendedCityIds)
  const displayRecommendedSearches = useMemo(
    () =>
      (isHotelMergeRecentSearchFlow && !areRecentSearchesReady) || preferRecentSearchesOnly
        ? []
        : recommendedSearches,
    [isHotelMergeRecentSearchFlow, areRecentSearchesReady, preferRecentSearchesOnly, recommendedSearches],
  )
  const [autocompleteValue, setAutocompleteValue] = useState<string | null>(null)
  const airportNestingEnabled = useFlightAirportNesting(typeAheadEndpoint)
  const typeAheadHorizon = useTypeAhead({
    searchValue: autocompleteValue,
    searchKey,
    onLoaded: () => null,
    typeAheadEndpoint,
    airportNestingEnabled,
    hasAISearch,
    hasLTRSearch,
  })

  type geoData = {
    cityID: string
    content: string
    itemName: string
    lat: number
    lon: number
    type: string
  }

  const geoData = current.context?.cityData as geoData | null

  const didFireSelect = useRef(false)
  const didFireRecentSearchImprovementImpression = useRef(false)
  const [autoKey, setAutoKey] = useState('auto-default')
  /** When user selects an item from the list, Base UI may call onInputValueChange with that value; skip updating autocompleteValue so we do not trigger a typeahead API call. */
  const lastSelectedItemNameRef = useRef<string | null>(null)
  /** Tracks the current input value (typed or selected) for use in onAiSearchClick.
   * Seeded from initialSelectedItem so pre-populated inputs (e.g. SEM landing pages)
   * carry the correct prompt when the user clicks "Ask" without typing first. */
  const currentSearchQueryRef = useRef<string>(initialSelectedItem?.itemName ?? '')

  /**
   * When geolocation resolves:
   * 1) Force a fresh <Autocomplete> instance by changing its `key`.
   *    The Horizon Autocomplete holds internal state (Downshift useCombobox + local useState),
   *    so a remount guarantees we clear input value, highlighted index, open/close state,
   *    and any pending suggestion state from before geo finished.
   * 2) Programmatically "select" the resolved city so the parent form receives it without
   *    requiring another user action.
   * 3) Guard with a ref so this runs once per resolution.
   *
   * Notes:
   * - Autocomplete manages internal items, selectedItem, overlay open state, and uses
   *   downshift’s useCombobox internally, not fully controlled from above—no single
   *   public “reset” API—hence the reliable `key`-flip remount.
   */

  useEffect(() => {
    if (!showCurrentLocation) return
    if (!current?.matches?.('resolved')) return
    const resolved = current.context?.cityData
    if (!resolved) return
    if (didFireSelect.current) return

    didFireSelect.current = true
    onItemSelect?.(resolved)
    // Defer setState to avoid synchronous setState in effect (cascading renders)
    const nextKey = `geo-${resolved.id ?? resolved.cityID}`
    queueMicrotask(() => setAutoKey(nextKey))
  }, [current, showCurrentLocation, onItemSelect])

  // Notify parent when geolocation fails so error can be displayed in form
  useEffect(() => {
    if (!showCurrentLocation || !onGeolocationError) return

    if (current.matches('rejected')) {
      const errorMsg = renderDisplayText(current, clientGUID)
      onGeolocationError(errorMsg)
    } else if (current.matches('idle') || current.matches('resolved')) {
      onGeolocationError(null)
    }
  }, [current, showCurrentLocation, clientGUID, onGeolocationError])

  const { autocompleteData: baseAutocompleteData, listType } = useMemo(
    () =>
      buildAutocompleteDataAndListType({
        autocompleteValue,
        mergeRecentAndRecommendedSearches,
        preferRecentSearchesOnly,
        recentSearches,
        recommendedSearches: displayRecommendedSearches,
        typeAheadItems: typeAheadHorizon.items,
      }),
    [
      mergeRecentAndRecommendedSearches,
      preferRecentSearchesOnly,
      recentSearches,
      displayRecommendedSearches,
      typeAheadHorizon.items,
      autocompleteValue,
    ],
  )

  const autocompleteData = useMemo(() => {
    if (!(showCurrentLocation && isMobile)) return baseAutocompleteData
    const display = renderDisplayText(current, clientGUID)
    const currentLocationItem = {
      ...geoData,
      content: 'Current Location',
      displayLine1: display,
      itemName: geoData?.itemName || '',
      type: 'GEO',
    }
    return [currentLocationItem, ...baseAutocompleteData]
  }, [geoData, current, clientGUID, baseAutocompleteData, isMobile, showCurrentLocation])

  // Derive filterChipData directly (no useState/useEffect needed)
  const firstData = autocompleteData[0]
  const hasValidCity = firstData && 'cityID' in firstData && cityIdExistsInTop20Filters(firstData.cityID)
  const currentCityId = hasValidCity ? firstData.cityID : undefined
  const filterChipData = currentCityId ? top20Filters[currentCityId as keyof typeof top20Filters] : undefined

  // Track previous city to clear selected filters when city changes
  const prevCityIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    const prevCityId = prevCityIdRef.current

    // Clear filters when switching cities (either to a different top20 or to non-top20)
    if (prevCityId !== undefined && prevCityId !== currentCityId) {
      setSelectedFilterChipData?.({
        amenities: new Set(),
        hotelStarLevel: new Set(),
        guestRating: new Set(),
        propertyTypes: new Set(),
        AMENITY: new Set(),
        HOTEL_STAR_LEVEL: new Set(),
        GUEST_RATING: new Set(),
        PROPERTY_TYPE: new Set(),
        ALL_BRANDS: new Set(),
        PROPERTY_THEME: new Set(),
      })
    }

    prevCityIdRef.current = currentCityId
  }, [currentCityId, setSelectedFilterChipData])

  const handleTypeAheadFeedback = useCallback(
    ({
      entered,
      id,
      itemName,
      displayName,
      type,
      selectedPosition,
      skipFeedbackCall,
      parentId,
      isIndented,
      selectedChildPosition,
    }: LOCATION_SEARCH_TYPE & {
      selectedPosition: number | null
      skipFeedbackCall?: boolean
      selectedChildPosition?: number | null
    }) => {
      if (skipFeedbackCall) return

      // swagger for feedback call api http://ny-htlindex-001:8080/typeahead/index/swagger-ui.html#/feedback-controller/typeaheadFeedbackWithSelectedPositionUsingGET
      const url = `${
        CONFIG.client.typeahead.feedback[typeAheadEndpoint]
      }/${id}/${encodeURIComponent(entered ?? 'null')}/${encodeURIComponent(
        itemName || displayName || '',
      )}/${selectedPosition ?? ''}`
      const queryParams = new URLSearchParams({
        language: 'en',
        locale: 'en-US',
        placeType: mapTypeToCode[type],
      })
      if (typeAheadEndpoint === 'flights') {
        if (isIndented) queryParams.set('isIndented', 'true')
        if (parentId) queryParams.set('parentId', parentId)
        if (selectedChildPosition !== null && selectedChildPosition !== undefined)
          queryParams.set('selectedChildPosition', String(selectedChildPosition))
      }

      const isHotel = typeAheadEndpoint === 'hotels' || typeAheadEndpoint === 'hotelsV2'
      if (isHotel) {
        void fetch(`${url}?${queryParams}`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            items: typeAheadHorizon.items,
            apiDuration: typeAheadHorizon.duration,
            source: typeAheadHorizon.source,
          }),
        })
      } else {
        void fetch(`${url}?${queryParams}`)
      }
    },
    [typeAheadEndpoint, typeAheadHorizon.items, typeAheadHorizon.duration, typeAheadHorizon.source],
  )

  const handleInputValueChange = useCallback(
    (inputValue: string) => {
      currentSearchQueryRef.current = inputValue
      if (lastSelectedItemNameRef.current !== null && inputValue === lastSelectedItemNameRef.current) {
        lastSelectedItemNameRef.current = null
        return
      }
      setAutocompleteValue(inputValue)
      if (inputValue === '') {
        onItemSelect?.(null)
      }
    },
    [onItemSelect],
  )

  type AutocompleteItem = (typeof autocompleteData)[number]

  const handleSelectItem = useCallback(
    (selectedItem: AutocompleteItem, highlightedIndex?: number) => {
      if ('isGroupHeader' in selectedItem && selectedItem.isGroupHeader) return

      if ('itemName' in selectedItem && typeof selectedItem.itemName === 'string') {
        lastSelectedItemNameRef.current = selectedItem.itemName
        currentSearchQueryRef.current = selectedItem.itemName
      }

      if ('content' in selectedItem && selectedItem.content === 'Current Location') {
        if (!geoData) send('REQUEST')
        else onItemSelect?.(selectedItem as LOCATION_SEARCH_TYPE)
        return
      }

      const selectedLocation = selectedItem as LOCATION_SEARCH_TYPE
      onItemSelect?.(selectedLocation)

      const { id: selectedId, parentId, isIndented } = selectedLocation
      const selectedChildPosition =
        typeAheadEndpoint === 'flights' && isIndented && parentId
          ? getSelectedChildPosition({ items: typeAheadHorizon.items, selectedId, parentId })
          : null

      const resolveClickType = resolveClickListTypeForItem(
        selectedItem as LOCATION_SEARCH_TYPE,
        listType,
        recentSearches,
        displayRecommendedSearches,
      )

      fireTypeaheadClickEvent(
        typeAheadEndpoint,
        selectedLocation.type === 'AI_QUERY' ? 'typein_ai_query' : resolveClickType,
      )

      const hasCurrentLocation = showCurrentLocation && isMobile
      const adjustedIndex =
        hasCurrentLocation && highlightedIndex != null ? highlightedIndex - 1 : highlightedIndex

      handleTypeAheadFeedback({
        ...selectedLocation,
        selectedPosition: adjustedIndex ?? null,
        selectedChildPosition,
      })
    },
    [
      geoData,
      isMobile,
      listType,
      recentSearches,
      displayRecommendedSearches,
      onItemSelect,
      showCurrentLocation,
      typeAheadEndpoint,
      send,
      handleTypeAheadFeedback,
      typeAheadHorizon.items,
    ],
  )

  const handleMenuOpen = useCallback(() => {
    const hasHotelRecents = useImprovedRecentSearchFlow
      ? areRecentSearchesReady && hasRecentSearches(recentSearches)
      : hasRecentSearches(recentSearches)

    // HTL_SEARCH_FRM_RECENT_SEARCH_IMPRV: manual SETI impression on autocomplete open (not page load).
    // HorizonHotelForm sets useSeti shouldFireImpression: false and this experiment is in seti.json
    // noImpression — bucketing happens on load, exposure is recorded here when the dropdown opens.
    // Fires at most once per mount. Per-bucket rules (33/33/33 + 1% CONTROL; DEFAULT ≈ CONTROL):
    //   VARIANT2 (preferRecentSearchesOnly): user has recents
    //   VARIANT1 (mergeRecentAndRecommendedSearches): user has recents and recommendations
    //   DEFAULT / CONTROL (neither flag): user has recents or recommendations
    const isControlRecentSearchFlow =
      isHotelEndpoint && !mergeRecentAndRecommendedSearches && !preferRecentSearchesOnly
    const shouldFireImpression =
      onRecentSearchImprovementImpression &&
      !didFireRecentSearchImprovementImpression.current &&
      ((preferRecentSearchesOnly && hasHotelRecents) ||
        (isHotelMergeRecentSearchFlow &&
          hasHotelRecents &&
          hasRecommendedSearches(displayRecommendedSearches)) ||
        (isControlRecentSearchFlow &&
          (hasHotelRecents || hasRecommendedSearches(displayRecommendedSearches))))

    if (shouldFireImpression) {
      didFireRecentSearchImprovementImpression.current = true
      onRecentSearchImprovementImpression()
    }
    const updatedListType = listType === 'typein' && !autocompleteValue ? undefined : listType
    fireAutocompleteOpenEvent(typeAheadEndpoint, updatedListType)
  }, [
    onRecentSearchImprovementImpression,
    isHotelEndpoint,
    mergeRecentAndRecommendedSearches,
    useImprovedRecentSearchFlow,
    isHotelMergeRecentSearchFlow,
    preferRecentSearchesOnly,
    areRecentSearchesReady,
    recentSearches,
    displayRecommendedSearches,
    listType,
    autocompleteValue,
    typeAheadEndpoint,
  ])

  type FilterChipClickItem = Parameters<
    NonNullable<React.ComponentProps<typeof Autocomplete>['handleFilterChipClick']>
  >[0]
  const handleFilterChipClick = useCallback(
    (item: FilterChipClickItem, selected: boolean) => {
      if (setSelectedFilterChipData) {
        setSelectedFilterChipData((prev: SelectedTopFilters) => {
          const { filterType, code, name } = item as CityFilterItem
          const newFilteredSet = new Set(prev[filterType])

          if (selected) {
            newFilteredSet.add(`${filterType}_${code}_${name}`)
            fireFilterBarChipGA4Event(filterType, name, true)
          } else {
            newFilteredSet.delete(`${filterType}_${code}_${name}`)
            fireFilterBarChipGA4Event(filterType, name, false)
          }
          return {
            ...prev,
            [filterType]: newFilteredSet,
          }
        })
      }
    },
    [setSelectedFilterChipData],
  )

  const memoizedSelectedFilterChipData = useMemo(() => {
    if (!selectedFilterChipData) return undefined
    return new Set([
      ...selectedFilterChipData.amenities,
      ...selectedFilterChipData.hotelStarLevel,
      ...selectedFilterChipData.guestRating,
      ...selectedFilterChipData.propertyTypes,
    ])
  }, [selectedFilterChipData])

  const onAiSearchClick = useCallback(() => {
    if (!aiSearchButtonVariant) return

    const { linkName } = AI_SEARCH_BUTTON_CONFIG[aiSearchButtonVariant]
    analytics.fireGA4Event({
      event: 'internal_link',
      attributes: {
        type: 'ai_mode_penny_search',
        link_name: linkName,
        page_name: 'homepage',
        product_name: 'penny',
      },
    })
    const prompt = currentSearchQueryRef.current
      ? encodeURIComponent(`Hotels near ${currentSearchQueryRef.current}`)
      : ''
    const query = prompt ? `?askPrompt=${prompt}` : ''
    window.open(`/penny${query}`, '_blank', 'noopener,noreferrer')
  }, [aiSearchButtonVariant])

  const rightIconButtonProps = useMemo(() => {
    if (!aiSearchButtonVariant) return undefined

    const { label: aiButtonLabel } = AI_SEARCH_BUTTON_CONFIG[aiSearchButtonVariant]
    return {
      children: <span className='text-primary-13'>{aiButtonLabel}</span>,
      onClick: onAiSearchClick,
      type: 'tertiary' as const,
      size: 'sm' as const,
      iconLeft: 'AiSearch' as const,
      slots: {
        textSlot: 'font-medium',
        iconSlot: 'relative -top-0.5',
      },
    }
  }, [aiSearchButtonVariant, onAiSearchClick])

  return (
    <Autocomplete
      key={autoKey}
      inputRef={ref}
      errorMessage={errorMessage}
      label={label}
      items={autocompleteData}
      onInputValueChange={handleInputValueChange}
      onSelectItem={handleSelectItem}
      defaultSelectedItem={initialSelectedItem}
      onMenuOpen={handleMenuOpen}
      filterChipData={displayFilterChips ? filterChipData : undefined}
      handleFilterChipClick={handleFilterChipClick}
      selectedFilterChipData={memoizedSelectedFilterChipData}
      collisionAvoidance={DEFAULT_COLLISION_AVOIDANCE}
      rightIconButtonProps={rightIconButtonProps}
    />
  )
})

export default AutocompleteWrapper
